import { Vector3 } from '@technology/web3d';
import { AlignmentNavigationStatus } from '@technology/web3d-plugin-trimbim';
import {
  AlignmentNavigationCapability,
  PlanarCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import { CapabilityRegistryContainer } from '@trimble/web3d';
import { getActiveNavCap } from './sync-source-helpers';

/**
 * Returns the meaningful sync position from an alignment source pane:
 * the alignment tool's current point along the path, cloned so it is safe
 * to hold across await boundaries.  Returns null when the pane has no
 * active `AlignmentNavigationCapability` or the capability is active but
 * not yet navigating (no alignment picked yet) — the tool's
 * `currentAlignmentPoint` is only populated while
 * {@link AlignmentNavigationStatus.Navigating}.  Callers should abort
 * sync on null.
 */
export function getAlignmentSyncPosition(
  pane: CapabilityRegistryContainer
): Vector3 | null {
  const navCap = getActiveNavCap(pane);
  if (!(navCap instanceof AlignmentNavigationCapability)) return null;
  if (navCap.status() !== AlignmentNavigationStatus.Navigating) return null;
  return navCap.currentAlignmentPoint.clone();
}

/**
 * Returns the meaningful sync position from a station/image source pane:
 * the canonical station position (cloned), with a fallback to the viewer
 * camera position when no station is selected or no
 * StationsNavigationCapability is active. Returns null only when the pane
 * has no viewer.
 *
 * `ImageCapabilityBase` extends `StationsNavigationCapability`, so this one
 * helper covers plain stations + Panorama + Planar — the whole subtree.
 */
export function getStationSyncPosition(
  pane: CapabilityRegistryContainer
): Vector3 | null {
  const viewer = pane.viewerWrapper()?.viewer;
  if (!viewer) return null;

  const navCap = getActiveNavCap(pane);
  if (navCap instanceof StationsNavigationCapability) {
    return (
      navCap.currentStationPosition()?.clone() ?? viewer.camera.position.clone()
    );
  }

  return viewer.camera.position.clone();
}

/**
 * Whether a station/image source pane's camera orientation carries a
 * real-world direction worth propagating to other panes. Returns false for
 * `PlanarCapability` (camera is fixed top-down, unrelated to the 3D scene);
 * true for plain stations and Panorama.
 */
export function isStationOrientationMeaningful(
  pane: CapabilityRegistryContainer
): boolean {
  return !(getActiveNavCap(pane) instanceof PlanarCapability);
}
